// SettingsPage - 账户设置页面 (增强安全设置)
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useMessage } from '../context/MessageContext';
import Avatar from 'boring-avatars';

import {
    Settings,
    User,
    Palette,
    Shield,
    Bell,
    Camera,
    Mail,
    Smartphone,
    Key,
    Monitor,
    Globe,
    Pencil,
    Sparkles,
    Check,
    Crown,
    LogOut,
    HelpCircle,

    Loader2
} from 'lucide-react';
import securityService from '../services/securityService';
import ChangePasswordModal from '../components/modals/ChangePasswordModal';
import ChangePhoneModal from '../components/modals/ChangePhoneModal';
import BindWechatModal from '../components/modals/BindWechatModal';

const SettingsPage = () => {
    const { user, token, logout, refreshProfile } = useAuth();
    const { showMessage } = useMessage();

    // 安全状态
    const [securityStatus, setSecurityStatus] = useState({
        has_password: false,
        has_phone: false,
        phone_masked: null,
        has_wechat: false,
        wechat_nickname: null,
    });
    const [loadingStatus, setLoadingStatus] = useState(true);

    // 弹窗状态
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [showPhoneModal, setShowPhoneModal] = useState(false);
    const [showWechatModal, setShowWechatModal] = useState(false);

    // 开关状态
    const [switchStates, setSwitchStates] = useState({
        'newFeature': true,
        'createComplete': true,
        'community': false,
        'system': true
    });

    // 用户名编辑
    const [isEditingUsername, setIsEditingUsername] = useState(false);
    const [usernameInput, setUsernameInput] = useState('');
    const [savingUsername, setSavingUsername] = useState(false);

    // 获取安全状态
    const fetchSecurityStatus = useCallback(async () => {
        if (!token) return;
        try {
            const data = await securityService.getSecurityStatus(token);
            setSecurityStatus(data);
        } catch (err) {
            console.error('获取安全状态失败:', err);
        } finally {
            setLoadingStatus(false);
        }
    }, [token]);

    useEffect(() => {
        fetchSecurityStatus();
    }, [fetchSecurityStatus]);

    const toggleSwitch = (key) => {
        setSwitchStates(prev => ({ ...prev, [key]: !prev[key] }));
    };

    // 保存用户名
    const handleSaveUsername = async () => {
        if (!usernameInput.trim()) {
            showMessage('error', '用户名不能为空');
            return;
        }
        if (usernameInput.trim().length < 3) {
            showMessage('error', '用户名至少3个字符');
            return;
        }
        setSavingUsername(true);
        try {
            await securityService.changeUsername(token, usernameInput.trim());
            showMessage('success', '用户名修改成功');
            setIsEditingUsername(false);
            refreshProfile?.();
            fetchSecurityStatus();
        } catch (err) {
            showMessage('error', err.response?.data?.error || '修改失败');
        } finally {
            setSavingUsername(false);
        }
    };

    // 修改密码成功回调
    const handlePasswordSuccess = () => {
        showMessage('success', '密码修改成功');
        fetchSecurityStatus();
    };

    // 换绑手机成功回调
    const handlePhoneSuccess = () => {
        showMessage('success', '手机换绑成功');
        fetchSecurityStatus();
        refreshProfile?.();
    };

    // 微信绑定/解绑成功回调
    const handleWechatSuccess = () => {
        showMessage('success', securityStatus.has_wechat ? '微信解绑成功' : '微信绑定成功');
        fetchSecurityStatus();
    };

    const colorStyles = {
        blue: { bg: 'bg-blue-50', text: 'text-blue-600', gradient: 'from-blue-500 to-blue-600' },
        purple: { bg: 'bg-purple-50', text: 'text-purple-600', gradient: 'from-purple-500 to-purple-600' },
        green: { bg: 'bg-green-50', text: 'text-green-600', gradient: 'from-green-500 to-green-600' },
        orange: { bg: 'bg-orange-50', text: 'text-orange-600', gradient: 'from-orange-400 to-orange-500' },
    };

    // 渲染安全设置项
    const renderSecurityItem = (icon, label, value, status, action, onAction, colors) => (
        <div className="flex items-center py-3 border-b border-gray-50 last:border-0">
            <div className={`w-8 h-8 rounded-lg ${colors.bg} flex items-center justify-center mr-3`}>
                {React.createElement(icon, { className: `w-4 h-4 ${colors.text}` })}
            </div>
            <div className="flex-1 min-w-0">
                <span className="text-sm font-medium text-gray-700">{label}</span>
                {value && <p className="text-xs text-gray-400">{value}</p>}
            </div>
            <div className="flex items-center gap-2">
                {status === 'verified' && (
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-green-50 text-green-600">
                        已{label.includes('密码') ? '设置' : '绑定'}
                    </span>
                )}
                {status === 'unset' && (
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-orange-50 text-orange-500">
                        未{label.includes('密码') ? '设置' : '绑定'}
                    </span>
                )}
                <button
                    onClick={onAction}
                    className={`text-xs font-semibold ${colors.text} hover:underline`}
                >
                    {action}
                </button>
            </div>
        </div>
    );

    // 渲染通知设置项
    const renderNotificationItem = (icon, label, switchKey, colors) => (
        <div className="flex items-center py-3 border-b border-gray-50 last:border-0">
            <div className={`w-8 h-8 rounded-lg ${colors.bg} flex items-center justify-center mr-3`}>
                {React.createElement(icon, { className: `w-4 h-4 ${colors.text}` })}
            </div>
            <div className="flex-1 min-w-0">
                <span className="text-sm font-medium text-gray-700">{label}</span>
            </div>
            <button
                onClick={() => toggleSwitch(switchKey)}
                className={`w-10 h-6 rounded-full p-0.5 transition-colors ${switchStates[switchKey] ? `bg-gradient-to-r ${colors.gradient}` : 'bg-gray-200'
                    }`}
            >
                <div className={`w-5 h-5 rounded-full bg-white shadow transition-transform ${switchStates[switchKey] ? 'translate-x-4' : ''
                    }`} />
            </button>
        </div>
    );

    return (
        <div className="w-full min-h-full bg-gray-50 p-6 md:p-8 overflow-y-auto">
            <div className="max-w-2xl mx-auto">
                {/* 页面标题 */}
                <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center shadow-lg">
                        <Settings className="w-5 h-5 text-white" />
                    </div>
                    <div>
                        <h1 className="text-xl font-black text-gray-900">账户设置</h1>
                        <p className="text-xs text-gray-400">管理账号安全与偏好设置</p>
                    </div>
                </div>

                {/* 用户卡片 */}
                <div className="bg-white rounded-xl border border-gray-100 p-4 mb-4 flex items-center gap-4">
                    <div className="relative">
                        <div className="w-14 h-14 rounded-xl overflow-hidden ring-2 ring-gray-100">
                            <Avatar
                                size={56}
                                name={String(user?.username || 'Guest')}
                                variant="beam"
                                colors={['#2563EB', '#3B82F6', '#60A5FA', '#5A67D8', '#93C5FD']}
                            />
                        </div>
                        <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-blue-500 rounded-md flex items-center justify-center cursor-pointer hover:bg-blue-600 transition">
                            <Camera className="w-2.5 h-2.5 text-white" />
                        </div>
                    </div>
                    <div className="flex-1">
                        {isEditingUsername ? (
                            <div className="flex items-center gap-2">
                                <input
                                    type="text"
                                    value={usernameInput}
                                    onChange={(e) => setUsernameInput(e.target.value)}
                                    placeholder="输入新用户名"
                                    className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    maxLength={20}
                                    autoFocus
                                />
                                <button
                                    onClick={handleSaveUsername}
                                    disabled={savingUsername}
                                    className="px-2 py-1 text-xs bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50"
                                >
                                    {savingUsername ? <Loader2 className="w-3 h-3 animate-spin" /> : '保存'}
                                </button>
                                <button
                                    onClick={() => setIsEditingUsername(false)}
                                    className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200"
                                >
                                    取消
                                </button>
                            </div>
                        ) : (
                            <div className="flex items-center gap-1.5">
                                <span className="font-bold text-gray-900">
                                    {user?.username || 'AI创作者'}
                                </span>
                                <Check className="w-3.5 h-3.5 text-blue-500" />
                                <span className="text-[9px] font-bold text-white bg-gradient-to-r from-amber-400 to-orange-500 px-1.5 py-0.5 rounded flex items-center gap-0.5">
                                    <Crown className="w-2.5 h-2.5" /> 创作者
                                </span>
                            </div>
                        )}
                    </div>
                    {!isEditingUsername && (
                        <button
                            onClick={() => {
                                setUsernameInput(user?.username || '');
                                setIsEditingUsername(true);
                            }}
                            className="px-3 py-1.5 rounded-lg bg-blue-500 text-white text-xs font-bold flex items-center gap-1 hover:bg-blue-600 transition"
                        >
                            <Pencil className="w-3 h-3" /> 编辑
                        </button>
                    )}
                </div>

                {/* 设置模块 */}
                <div className="space-y-4">
                    {/* 安全设置 - 重点增强 */}
                    <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                        <div className="px-4 py-3 border-b border-gray-50 flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-lg ${colorStyles.green.bg} flex items-center justify-center`}>
                                <Shield className={`w-4 h-4 ${colorStyles.green.text}`} />
                            </div>
                            <h3 className="text-sm font-bold text-gray-800">安全设置</h3>
                            {loadingStatus && (
                                <Loader2 className="w-4 h-4 text-gray-400 animate-spin ml-auto" />
                            )}
                        </div>
                        <div className="px-4">
                            {renderSecurityItem(
                                Key,
                                '登录密码',
                                securityStatus.has_password ? '••••••••' : '未设置密码，建议设置',
                                securityStatus.has_password ? 'verified' : 'unset',
                                securityStatus.has_password ? '修改' : '设置',
                                () => setShowPasswordModal(true),
                                colorStyles.green
                            )}
                            {renderSecurityItem(
                                Smartphone,
                                '绑定手机',
                                securityStatus.has_phone ? securityStatus.phone_masked : '未绑定，用于找回密码',
                                securityStatus.has_phone ? 'verified' : 'unset',
                                securityStatus.has_phone ? '换绑' : '绑定',
                                () => setShowPhoneModal(true),
                                colorStyles.green
                            )}
                            {renderSecurityItem(
                                Mail,
                                '微信绑定',
                                securityStatus.has_wechat ? securityStatus.wechat_nickname : '未绑定，可使用微信登录',
                                securityStatus.has_wechat ? 'verified' : 'unset',
                                securityStatus.has_wechat ? '解绑' : '绑定',
                                () => setShowWechatModal(true),
                                colorStyles.green
                            )}
                        </div>
                    </div>

                    {/* 账号信息 */}
                    {/* <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                        <div className="px-4 py-3 border-b border-gray-50 flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-lg ${colorStyles.blue.bg} flex items-center justify-center`}>
                                <User className={`w-4 h-4 ${colorStyles.blue.text}`} />
                            </div>
                            <h3 className="text-sm font-bold text-gray-800">账号信息</h3>
                        </div>
                        <div className="px-4">
                            <div className="flex items-center py-3 border-b border-gray-50">
                                <div className={`w-8 h-8 rounded-lg ${colorStyles.blue.bg} flex items-center justify-center mr-3`}>
                                    <User className={`w-4 h-4 ${colorStyles.blue.text}`} />
                                </div>
                                <span className="text-sm font-medium text-gray-700 flex-1">用户名</span>
                                <span className="text-sm text-gray-500">{user?.username || 'N/A'}</span>
                            </div>
                            <div className="flex items-center py-3">
                                <div className={`w-8 h-8 rounded-lg ${colorStyles.blue.bg} flex items-center justify-center mr-3`}>
                                    <Globe className={`w-4 h-4 ${colorStyles.blue.text}`} />
                                </div>
                                <span className="text-sm font-medium text-gray-700 flex-1">用户ID</span>
                                <span className="text-sm text-gray-500">{user?.id || 'N/A'}</span>
                            </div>
                        </div>
                    </div> */}

                    {/* 创作偏好 */}
                    {/* <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                        <div className="px-4 py-3 border-b border-gray-50 flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-lg ${colorStyles.purple.bg} flex items-center justify-center`}>
                                <Palette className={`w-4 h-4 ${colorStyles.purple.text}`} />
                            </div>
                            <h3 className="text-sm font-bold text-gray-800">创作偏好</h3>
                        </div>
                        <div className="px-4">
                            <div className="flex items-center py-3 border-b border-gray-50">
                                <div className={`w-8 h-8 rounded-lg ${colorStyles.purple.bg} flex items-center justify-center mr-3`}>
                                    <Monitor className={`w-4 h-4 ${colorStyles.purple.text}`} />
                                </div>
                                <span className="text-sm font-medium text-gray-700 flex-1">默认视频比例</span>
                                <div className="flex gap-1">
                                    {['9:16', '16:9', '1:1'].map(opt => (
                                        <button
                                            key={opt}
                                            className={`px-2.5 py-1 rounded-md text-xs font-medium transition-all ${opt === '9:16'
                                                ? `${colorStyles.purple.bg} ${colorStyles.purple.text}`
                                                : 'bg-gray-50 text-gray-400 hover:bg-gray-100'
                                                }`}
                                        >
                                            {opt}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div className="flex items-center py-3">
                                <div className={`w-8 h-8 rounded-lg ${colorStyles.purple.bg} flex items-center justify-center mr-3`}>
                                    <Sparkles className={`w-4 h-4 ${colorStyles.purple.text}`} />
                                </div>
                                <span className="text-sm font-medium text-gray-700 flex-1">默认风格</span>
                                <div className="flex gap-1">
                                    {['写实', '动漫', '油画'].map(opt => (
                                        <button
                                            key={opt}
                                            className={`px-2.5 py-1 rounded-md text-xs font-medium transition-all ${opt === '写实'
                                                ? `${colorStyles.purple.bg} ${colorStyles.purple.text}`
                                                : 'bg-gray-50 text-gray-400 hover:bg-gray-100'
                                                }`}
                                        >
                                            {opt}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div> */}

                    {/* 消息通知 */}
                    <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                        <div className="px-4 py-3 border-b border-gray-50 flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-lg ${colorStyles.orange.bg} flex items-center justify-center`}>
                                <Bell className={`w-4 h-4 ${colorStyles.orange.text}`} />
                            </div>
                            <h3 className="text-sm font-bold text-gray-800">消息通知</h3>
                        </div>
                        <div className="px-4">
                            {renderNotificationItem(Bell, '新功能通知', 'newFeature', colorStyles.orange)}
                            {renderNotificationItem(Sparkles, '创作完成提醒', 'createComplete', colorStyles.orange)}
                            {renderNotificationItem(User, '社区互动', 'community', colorStyles.orange)}
                            {renderNotificationItem(Mail, '系统公告', 'system', colorStyles.orange)}
                        </div>
                    </div>
                </div>

                {/* 底部操作 */}
                <div className="mt-6 flex justify-center gap-3">
                    <button className="px-4 py-2 rounded-lg bg-gray-100 text-gray-600 text-xs font-semibold flex items-center gap-1.5 hover:bg-gray-200 transition">
                        <HelpCircle className="w-3.5 h-3.5" /> 帮助中心
                    </button>
                    <button
                        onClick={logout}
                        className="px-4 py-2 rounded-lg bg-red-50 text-red-500 text-xs font-semibold flex items-center gap-1.5 hover:bg-red-100 transition"
                    >
                        <LogOut className="w-3.5 h-3.5" /> 退出登录
                    </button>
                </div>

                {/* 版本信息 */}
                <div className="mt-8 text-center">
                    <p className="text-[10px] text-gray-300">AI MEND v2.0 · © 2025</p>
                </div>
            </div>

            {/* 弹窗 */}
            <ChangePasswordModal
                isOpen={showPasswordModal}
                onClose={() => setShowPasswordModal(false)}
                hasPassword={securityStatus.has_password}
                onSuccess={handlePasswordSuccess}
            />
            <ChangePhoneModal
                isOpen={showPhoneModal}
                onClose={() => setShowPhoneModal(false)}
                currentPhone={securityStatus.has_phone ? securityStatus.phone_masked : null}
                onSuccess={handlePhoneSuccess}
            />
            <BindWechatModal
                isOpen={showWechatModal}
                onClose={() => setShowWechatModal(false)}
                isBound={securityStatus.has_wechat}
                wechatNickname={securityStatus.wechat_nickname}
                onSuccess={handleWechatSuccess}
            />
        </div>
    );
};

export default SettingsPage;
